import _defaultTo from 'lodash/defaultTo';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _some from 'lodash/some';
import _sortBy from 'lodash/sortBy';
import _pick from 'lodash/pick';
// Serialization
import { deserialize, serialize } from 'serializr';
import { installationSchema } from './schemas';
// Permissions
import permissions from '../constants/Permissions';
// Models
import mEntity from './entity';
// Custom
import { SEARCHFIELD_TYPES } from '../constants/App';

/**
* Serialization
*/
const serializerKey = 'installation';

const deserializeInstallation = (data) => deserialize(installationSchema, data);

const serializeInstallation = ({
  companyId, name, address, timezone, location, production, antipassback,
}) => ({
  [serializerKey]: {
    ...serialize(installationSchema, {
      ...(companyId && { company_id: companyId }),
      name: _defaultTo(name, null),
      ...(address && { address }),
      timezone: _defaultTo(timezone, null),
      ...(location && { location }),
      ...(!_isNil(production) && { production }),
      ...(!_isNil(antipassback) && {
        antipassback: {
          enabled_entrance: antipassback.enabledEntrance,
          enabled_exit: antipassback.enabledExit,
          required_entrance: antipassback.requiredEntrance,
          required_exit: antipassback.requiredExit,
        },
      }),
    }),
  },
});

/**
* Lists
*/
const installationAsyncOperationsPeriods = [
  {
    labelId: 'period.24h',
    value: '1',
    days: 1,
    subtractor: { hours: 24 },
  },
  {
    labelId: 'period.3d',
    value: '2',
    days: 3,
    subtractor: { days: 3 },
  },
  {
    labelId: 'period.1w',
    value: '3',
    days: 7,
    subtractor: { days: 7 },
  },
];

/**
* Utils
*/
const installationTitle = (installationData) => _get(installationData, 'name', '');

const installationHasMasterEntity = (installationData) => (installationData ? _some(installationData.entities, mEntity.isMaster) : false);

const installationIsAdminEntity = (entity) => (mEntity.isMaster(entity) || mEntity.hostsOneparkBookings(entity));

const installationHasHostOpBookingsEntity = (installationData) => (installationData ? _some(installationData.entities, mEntity.hostsOneparkBookings) : false);

const installationSortedEntities = (installationData) => {
  if (!installationData) {
    return [];
  }
  return _sortBy(
    installationData.entities,
    [
      (entity) => {
        if (mEntity.isMaster(entity)) {
          return 0;
        }
        if (mEntity.hostsOneparkBookings(entity)) {
          return 1;
        }
        return 2;
      },
      'name',
    ],
  );
};

const installationDefaultEntity = (installationData) => (installationData ? installationSortedEntities(installationData)[0] : {});

const installationAsyncOperationsErrorsQuantityLevel = (errorsPerDay) => {
  // Note: value returned is aimed to be used in colors statusPalette
  if (errorsPerDay > 20) {
    return 'error';
  }
  if (errorsPerDay > 5) {
    return 'warning';
  }
  return 'success';
};
const installationAsyncOperationsSuccessRate = (successRate, errorsQuantity) => {
  // No async operation => 'success'
  if (successRate === 0 && errorsQuantity === 0) {
    return 'success';
  }
  // Note: value returned is aimed to be used in colors statusPalette
  if (successRate < 75) {
    return 'error';
  }
  if (successRate < 90) {
    return 'warning';
  }
  return 'success';
};

const installationContext = (installationData) => ({
  ..._pick(installationData, ['timezone']),
});

/**
* Model
*/
const installation = {
  // Name and icon
  name: 'installation',
  icon: 'installation',

  // Serialization
  deserialize: deserializeInstallation,
  serialize: serializeInstallation,

  // Lists
  asyncOperationsPeriods: installationAsyncOperationsPeriods,

  // Utils
  title: installationTitle,
  hasMasterEntity: installationHasMasterEntity,
  hasHostOpBookingsEntity: installationHasHostOpBookingsEntity,
  isAdminEntity: installationIsAdminEntity,
  sortedEntities: installationSortedEntities,
  defaultEntity: installationDefaultEntity,
  asyncOperationsErrorsQuantityLevel: installationAsyncOperationsErrorsQuantityLevel,
  asyncOperationsSuccessRate: installationAsyncOperationsSuccessRate,
  context: installationContext,

  // List features
  listFeatures: ({ useAdminSort } = {}) => ({
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: useAdminSort ? [
        { field: 'async_operations_error_quantity', dir: 'desc' },
        { field: 'async_operations_success_rate', dir: 'desc' },
        { field: 'name', dir: 'asc' },
      ] : [
        { field: 'name', dir: 'asc' },
      ],
    },

    // Autocomplete filter
    autoCompleteFilter: {
      field: 'name',
      operator: SEARCHFIELD_TYPES.string.defaultOperator(),
    },

    // Columns
    columnsQuantity: (hasPermission) => ({
      xs: 1,
      sm: 1,
      md: 2,
      lg: 3 + (4 * (hasPermission({ needed: permissions.pAdmin }) | 0)),
      xl: 3 + (4 * (hasPermission({ needed: permissions.pAdmin }) | 0)),
    }),
    columns: [
      {
        key: 'name',
        labelId: 'field.name',
        field: 'name',
        sortable: true,
      },
      {
        key: 'devices',
        labelId: 'field.devices',
        field: 'devices',
        sortable: false,
        className: 'hidden-sm-down',
      },
      {
        key: 'insertedAt',
        labelId: 'field.creationDate',
        field: 'inserted_at',
        sortable: true,
        className: 'table-content-3 hidden-md-down',
      },
      {
        key: 'asyncOperationsErrorsQuantity',
        permissions: {
          needed: permissions.pAdmin,
        },
        labelId: 'field.asyncOperationsErrorsQuantity',
        field: 'async_operations_error_quantity',
        sortable: true,
        className: 'table-content-2 hidden-md-down',
      },
      {
        key: 'asyncOperationsSuccessRate',
        permissions: {
          needed: permissions.pAdmin,
        },
        labelId: 'field.asyncOperationsSuccessRate',
        field: 'async_operations_success_rate',
        sortable: true,
        className: 'table-content-2 hidden-md-down',
      },
      {
        key: 'lastAsyncOperationSuccess',
        permissions: {
          needed: permissions.pAdmin,
        },
        labelId: 'field.lastAsyncOperationSuccess',
        field: 'last_success_async_operation',
        sortable: false,
        className: 'hidden-md-down',
      },
      {
        key: 'lastAsyncOperationError',
        permissions: {
          needed: permissions.pAdmin,
        },
        labelId: 'field.lastAsyncOperationError',
        field: 'last_error_async_operation',
        sortable: true,
        className: 'hidden-md-down',
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'name',
        labelId: 'field.name',
        field: 'name',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'name',
        },
      },
      {
        key: 'address',
        labelId: 'field.addressZipcodeCity',
        field: 'address',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'address',
        },
      },
      {
        key: 'country',
        labelId: 'field.country',
        field: 'country',
        initial: 'all',
        isUndefined: (value) => value === 'all',
        needGetOnInit: false,
        isIndependant: true,
        type: SEARCHFIELD_TYPES.enum,
        filter: {
          field: 'country',
        },
      },
      {
        key: 'device',
        permissions: {
          needed: permissions.pDevice.read,
          permissionsOrContext: 'userPermissions',
        },
        labelId: 'field.device',
        field: 'device',
        alias: 'device_id',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: true,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'installation_device_maps.device_id',
          key: 'id',
        },
      },
      {
        key: 'timeout',
        labelId: 'field.withDeviceTimeout',
        field: 'timeout',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.boolean,
        filter: {
          field: 'timeout',
        },
      },
      {
        key: 'companyId',
        permissions: {
          needed: permissions.pAdmin,
        },
        labelId: 'field.company',
        field: 'companyId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'company_id',
        },
      },
    ],
  }),
};

module.exports = installation;



// WEBPACK FOOTER //
// ./models/installation.js